import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ThemeToggle from '@/theme/ThemeToggle';
import { NAV_LINKS } from '../content';

type Props = { logged: boolean; isAdmin: boolean };

export default function Header({ logged, isAdmin }: Props) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <header
      className={`sticky top-0 z-50 transition-colors duration-200 ${
        scrolled ? 'bg-bg border-b border-border' : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="max-w-[1040px] mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-md bg-accentSoft border border-accentLine flex items-center justify-center">
            <i className="ri-stack-line text-accent text-base" />
          </span>
          <span className="text-textp font-semibold tracking-tight">Sourcetape</span>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <a
              key={link.label}
              href={link.href.startsWith('#') ? `/${link.href}` : link.href}
              className="whitespace-nowrap text-sm text-texts hover:text-textp px-3 py-1.5 rounded-md transition-colors"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          {logged ? (
            <>
              <Link to="/dashboard" className="whitespace-nowrap hidden md:flex text-sm text-texts hover:text-textp px-3 py-1.5 transition-colors">
                Dashboard
              </Link>
              {isAdmin && (
                <Link to="/admin" data-testid="superadmin-link" className="whitespace-nowrap hidden md:flex text-sm text-texts hover:text-textp px-3 py-1.5 transition-colors">
                  Superadmin
                </Link>
              )}
            </>
          ) : (
            <Link to="/login" className="whitespace-nowrap hidden md:flex text-sm text-texts hover:text-textp px-3 py-1.5 transition-colors">
              Sign in
            </Link>
          )}
          <ThemeToggle className="px-3 py-1.5 rounded-md" />
          {logged ? (
            <Link
              to="/dashboard"
              data-testid="header-get-started"
              className="whitespace-nowrap flex items-center gap-1.5 bg-accent hover:bg-accent-hover text-accent-text text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
            >
              Dashboard
              <i className="ri-arrow-right-line" />
            </Link>
          ) : (
            <a
              href="/#top"
              data-testid="header-get-started"
              className="whitespace-nowrap flex items-center gap-1.5 bg-accent hover:bg-accent-hover text-accent-text text-sm font-medium px-4 py-1.5 rounded-lg transition-colors"
            >
              Get started
              <i className="ri-arrow-right-line" />
            </a>
          )}
        </div>
      </div>
    </header>
  );
}
